import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Card,
  Chip,
  Checkbox,
  getContrastTextColor,
  Input,
  Spinner,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  useTabParam,
  DateField,
  holdsPlan,
} from '@structural-climbing/ui'
import { useGymThemeContext } from '../layouts/useGymTheme'
import { usePaginatedMembers, MEMBERS_PAGE_SIZE } from '../features/members/usePaginatedMembers'
import { MemberSearchInput } from '../features/members/MemberSearchInput'
import { useCheckIn } from '../features/checkIn/useCheckIn'
import { useCheckOut } from '../features/checkIn/useCheckOut'
import { scanBreadcrumb, syncScanDebugFromUrl } from '../features/checkIn/scanTelemetry'
import { MemberAvatar } from '../features/members/MemberAvatar'
import { MemberName } from '../features/members/MemberName'
import { ClubMemberStar } from '../features/members/ClubMemberStar'
import { MemberRow, ChildrenToggle } from '../features/members/MemberRow'
import { AssignPlanModal } from '../features/members/AssignPlanModal'
import { MemberProfileModal } from '../features/members/MemberProfileModal'
import { MemberIdentity } from '../features/members/MemberIdentity'
import type { SubscriptionStatus } from '@structural-climbing/ui'
import { memberDisplayName } from '../features/members/memberDisplayName'
import { PageTabs } from '../components/PageTabs'
import { PageHeader } from '../components/PageHeader'
import { ListPages } from '../components/ListPages'
import { stackOnPhone } from '../components/stackOnPhone'
import { useIsMobile } from '../lib/useIsMobile'
import { usePlanLine } from '../features/members/usePlanLine'
import { QrScanner } from '../features/checkIn/QrScanner'
import { CheckMark } from '../features/checkIn/CheckMark'
import { ScanCard, useQuickScan } from '../features/checkIn/QuickScan'

const SCAN_TABS = ['members', 'qr', 'history'] as const

interface HistoryCheckIn {
  check_in_id: string
  user_id: string | null
  child_id: string | null
  member_name: string
  email: string | null
  nickname: string | null
  avatar_ext: string | null
  avatar_updated_at: string | null
  checked_in_at: string
  checked_out_at: string | null
  is_guest: boolean
  // Absent until the Worker ships the first-visit marking.
  is_first_visit?: boolean
  total_count: number
  /** The parent or guardian to call, for someone under 18. */
  guardian?: { name: string; phone: string | null } | null
}

function formatDuration(
  checkedInAt: string,
  checkedOutAt: string | null,
  activeLabel: string,
): string {
  if (!checkedOutAt) return activeLabel
  const ms = new Date(checkedOutAt).getTime() - new Date(checkedInAt).getTime()
  const totalMinutes = Math.floor(ms / 60000)
  if (totalMinutes < 1) return '<1m'
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 0) return `${minutes}m`
  if (minutes === 0) return `${hours}h`
  return `${hours}h ${minutes}m`
}

const HISTORY_PAGE_SIZE = 20
const HISTORY_SEARCH_DEBOUNCE_MS = 300

function CheckInHistoryTab() {
  const isMobile = useIsMobile()
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const { primaryColor } = useGymThemeContext()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [historyDate, setHistoryDate] = useState(() => {
    const d = new Date()
    // Local date, not toISOString() — that is UTC and can name the wrong day.
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })
  const [historySearch, setHistorySearch] = useState('')
  // In the URL so the dashboard's "First visits today" card can open it set.
  const [firstParam, setFirstParam] = useTabParam('first', ['0', '1'] as const, '0')
  const firstOnly = firstParam === '1'
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [historyRows, setHistoryRows] = useState<HistoryCheckIn[]>([])
  const [historyTotal, setHistoryTotal] = useState<number | null>(null)
  const [historyCheckIns, setHistoryCheckIns] = useState(0)
  const [historyPage, setHistoryPage] = useState(1)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [profileTarget, setProfileTarget] = useState<string | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(historySearch), HISTORY_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [historySearch])

  const fetchPage = useCallback(
    async (date: string, search: string, first: boolean, page: number) => {
      setHistoryLoading(true)
      try {
        const token = await getAccessTokenSilently()
        const params = new URLSearchParams({
          date,
          limit: String(HISTORY_PAGE_SIZE),
          offset: String((page - 1) * HISTORY_PAGE_SIZE),
        })
        if (search) params.set('search', search)
        if (first) params.set('first', '1')
        const res = await fetch(`${apiUrl}/check-ins/history?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) return
        const data = (await res.json()) as {
          check_ins: HistoryCheckIn[]
          total: number
          total_check_ins: number
          has_more: boolean
        }
        setHistoryRows(data.check_ins)
        setHistoryTotal(data.total)
        setHistoryCheckIns(data.total_check_ins)
        setHistoryPage(page)
      } finally {
        setHistoryLoading(false)
      }
    },
    [apiUrl, getAccessTokenSilently],
  )

  // On open and on every filter change, from page 1. One effect: two (a
  // first-load one and a filter one) both fired on open and read the day twice.
  useEffect(() => {
    void fetchPage(historyDate, debouncedSearch, firstOnly, 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyDate, debouncedSearch, firstOnly])

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <DateField
          size="sm"
          label={t('scan.historyDate')}
          value={historyDate}
          onValueChange={setHistoryDate}
          className="w-44"
        />
        <Input
          size="sm"
          placeholder={t('scan.historySearch')}
          value={historySearch}
          onValueChange={setHistorySearch}
          className="w-full max-w-xs"
        />
        <Checkbox
          size="sm"
          isSelected={firstOnly}
          onValueChange={(on) => setFirstParam(on ? '1' : '0')}
          className="mb-1"
        >
          {t('scan.firstVisitsOnly')}
        </Checkbox>
        {historyTotal !== null && (
          <p className="text-lg font-bold">
            <span>{t('scan.historyMembers', { count: historyTotal })}</span>
            <span className="px-1 text-foreground/40">·</span>
            <span>{t('scan.historyTotal', { count: historyCheckIns })}</span>
          </p>
        )}
      </div>

      {historyLoading && historyRows.length === 0 ? (
        <div className="flex justify-center py-8">
          <Spinner size="sm" color="primary" />
        </div>
      ) : (
        <div className={`overflow-x-auto transition-opacity ${historyLoading ? 'opacity-60' : ''}`}>
          <Table removeWrapper aria-label={t('scan.historyTableLabel')} classNames={stackOnPhone()}>
            <TableHeader>
              <TableColumn>{t('scan.historyMember')}</TableColumn>
              <TableColumn>{t('scan.historyCheckIn')}</TableColumn>
              <TableColumn>{t('scan.historyCheckOut')}</TableColumn>
              <TableColumn>{t('scan.historyDuration')}</TableColumn>
            </TableHeader>
            <TableBody emptyContent={t('scan.historyNoResults')}>
              {historyRows.map((row) => (
                <TableRow key={row.check_in_id}>
                  <TableCell className="max-md:basis-full">
                    <div className="flex items-center gap-2">
                      <button
                        className={`flex min-w-0 items-center text-left${row.user_id ? ' cursor-pointer' : ''}`}
                        onClick={() => row.user_id && setProfileTarget(row.user_id)}
                        title={row.user_id ? t('members.viewProfile') : undefined}
                        disabled={!row.user_id}
                      >
                        <MemberIdentity
                          userId={row.user_id}
                          name={row.member_name}
                          nickname={row.nickname}
                          email={row.email}
                          avatarExt={row.avatar_ext}
                          avatarUpdatedAt={row.avatar_updated_at}
                          primaryColor={primaryColor}
                          size="sm"
                        />
                      </button>
                      {row.is_guest && (
                        <span className="shrink-0 rounded-full bg-warning/10 px-1.5 py-0.5 text-xs text-warning">
                          {t('dashboard.guest')}
                        </span>
                      )}
                      {row.is_first_visit && (
                        <span className="shrink-0 rounded-full bg-success/10 px-1.5 py-0.5 text-xs text-success">
                          {t('scan.firstVisit')}
                        </span>
                      )}
                      {row.total_count > 1 && (
                        <span className="shrink-0 rounded-full bg-default/20 px-1.5 py-0.5 text-xs text-foreground/50">
                          +{row.total_count - 1}
                        </span>
                      )}
                    </div>
                    {row.guardian && (
                      <p className="mt-0.5 text-xs text-foreground/60">
                        {t('dashboard.guardianLine', { name: row.guardian.name })}
                        {row.guardian.phone && ` · ${row.guardian.phone}`}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {isMobile && (
                      <span className="text-foreground/50">{t('scan.historyCheckIn')}: </span>
                    )}
                    {new Date(row.checked_in_at).toLocaleTimeString(undefined, {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </TableCell>
                  <TableCell className="text-sm">
                    {isMobile && (
                      <span className="text-foreground/50">{t('scan.historyCheckOut')}: </span>
                    )}
                    {row.checked_out_at
                      ? new Date(row.checked_out_at).toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {isMobile && (
                      <span className="text-foreground/50">{t('scan.historyDuration')}: </span>
                    )}
                    {formatDuration(row.checked_in_at, row.checked_out_at, t('scan.historyActive'))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ListPages
        page={historyPage}
        pageSize={HISTORY_PAGE_SIZE}
        total={historyTotal ?? 0}
        onPageChange={(next) => void fetchPage(historyDate, debouncedSearch, firstOnly, next)}
        isLoading={historyLoading}
      />

      {profileTarget && (
        <MemberProfileModal userId={profileTarget} onClose={() => setProfileTarget(null)} />
      )}
    </div>
  )
}

// --- Members list tab ---

function MembersCheckInList() {
  const { t } = useTranslation()
  const { primaryColor } = useGymThemeContext()
  const [search, setSearch] = useState('')
  // One page from the server at a time, searched there too: a gym with
  // thousands of members no longer downloads all of them, and all their plans,
  // every time the desk opens this tab.
  const {
    members,
    total,
    page,
    goToPage,
    checkedInMap,
    childCheckedInMap,
    plans,
    loading,
    reload,
  } = usePaginatedMembers(search, 'all')
  const { checkIn, stateFor } = useCheckIn()
  const { checkOut, isCheckingOut, isAlreadyCheckedOut } = useCheckOut(reload)

  const planLine = usePlanLine()

  const [profileTarget, setProfileTarget] = useState<string | null>(null)

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [assignTarget, setAssignTarget] = useState<{
    memberEmail: string
    memberUserId: string
    memberName?: string
    childId?: string
    childName?: string
  } | null>(null)

  /**
   * The children the desk can check in: those with a plan at this gym. A child
   * who never had one here cannot be checked in, and is not listed.
   */
  function checkInChildren(member: (typeof members)[number]) {
    return member.children.flatMap((c) =>
      c.subscription
        ? [
            {
              id: c.id,
              name:
                [c.first_name, c.last_name].filter(Boolean).join(' ') || t('members.childBadge'),
              sub: c.subscription,
            },
          ]
        : [],
    )
  }

  function toggleExpand(userId: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  /** What the row offers when the plan does not let them in: renew or sell one. */
  function sellButton(
    status: SubscriptionStatus | undefined,
    target: NonNullable<typeof assignTarget>,
  ) {
    if (holdsPlan(status)) return null
    return (
      <Button
        size="sm"
        variant="light"
        color="primary"
        className="font-medium"
        onPress={() => setAssignTarget(target)}
      >
        {status ? t('members.renewPlan') : t('members.sellPlan')}
      </Button>
    )
  }

  function renderCheckInActions(
    userId: string,
    subStatus: SubscriptionStatus | undefined,
    inactiveAction: ReactNode,
  ) {
    const ciState = stateFor(userId)
    const activeEntry = checkedInMap.get(userId)
    const alreadyIn = ciState === 'already_in' || !!activeEntry
    if (ciState === 'loading') {
      return (
        <Button size="sm" variant="flat" color="primary" isDisabled>
          <Spinner size="sm" color="current" />
        </Button>
      )
    }
    if (ciState === 'success') {
      return (
        <span className="rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
          {t('scan.checkInSuccess')}
        </span>
      )
    }
    if (alreadyIn && activeEntry) {
      if (isAlreadyCheckedOut(activeEntry.checkInId)) {
        return (
          <span className="rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
            {t('dashboard.alreadyCheckedOut')}
          </span>
        )
      }
      return (
        <Button
          size="sm"
          variant="bordered"
          className="border-1"
          isDisabled={isCheckingOut(activeEntry.checkInId)}
          onClick={() => void checkOut(activeEntry.checkInId, activeEntry.isGuest)}
        >
          {isCheckingOut(activeEntry.checkInId)
            ? t('dashboard.checkingOut')
            : t('dashboard.checkOut')}
        </Button>
      )
    }
    if (alreadyIn) {
      return (
        <Button size="sm" variant="flat" isDisabled>
          {t('scan.alreadyIn')}
        </Button>
      )
    }
    if (subStatus !== 'active') return inactiveAction
    return (
      <Button
        size="sm"
        variant={subStatus === 'active' ? 'solid' : 'flat'}
        color="primary"
        isDisabled={subStatus !== 'active'}
        onClick={() => void checkIn(userId)}
        startContent={subStatus === 'active' ? <CheckMark /> : undefined}
      >
        {t('scan.manualCheckIn')}
      </Button>
    )
  }

  function renderChildCheckInActions(
    userId: string,
    childId: string,
    childSub: SubscriptionStatus | undefined,
    inactiveAction: ReactNode,
  ) {
    const ciState = stateFor(userId, childId)
    const activeEntry = childCheckedInMap.get(childId)
    const alreadyIn = ciState === 'already_in' || !!activeEntry

    if (ciState === 'loading') {
      return (
        <Button size="sm" variant="flat" color="primary" isDisabled>
          <Spinner size="sm" color="current" />
        </Button>
      )
    }
    if (ciState === 'success') {
      return (
        <span className="rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
          {t('scan.checkInSuccess')}
        </span>
      )
    }
    if (alreadyIn && activeEntry) {
      if (isAlreadyCheckedOut(activeEntry.checkInId)) {
        return (
          <span className="rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
            {t('dashboard.alreadyCheckedOut')}
          </span>
        )
      }
      return (
        <Button
          size="sm"
          variant="bordered"
          className="border-1"
          isDisabled={isCheckingOut(activeEntry.checkInId)}
          onClick={() => void checkOut(activeEntry.checkInId, false)}
        >
          {isCheckingOut(activeEntry.checkInId)
            ? t('dashboard.checkingOut')
            : t('dashboard.checkOut')}
        </Button>
      )
    }
    if (alreadyIn) {
      return (
        <Button size="sm" variant="flat" isDisabled>
          {t('scan.alreadyIn')}
        </Button>
      )
    }
    if (childSub !== 'active') return inactiveAction
    return (
      <Button
        size="sm"
        variant="flat"
        color="primary"
        isDisabled={childSub !== 'active'}
        onClick={() => void checkIn(userId, childId)}
        style={
          childSub === 'active' && primaryColor
            ? { backgroundColor: primaryColor, color: getContrastTextColor(primaryColor) }
            : undefined
        }
      >
        {t('scan.manualCheckIn')}
      </Button>
    )
  }

  return (
    <>
      <div className="mb-4">
        <MemberSearchInput value={search} onValueChange={setSearch} className="w-full" />
      </div>

      {loading && members.length === 0 ? (
        <div className="flex justify-center py-8">
          <Spinner size="sm" color="primary" />
        </div>
      ) : members.length === 0 ? (
        <p className="py-8 text-center text-sm text-foreground/40">
          {t('scan.noMembersToCheckIn')}
        </p>
      ) : (
        <Card
          shadow="sm"
          className={`divide-y divide-divider px-4 transition-opacity ${loading ? 'opacity-60' : ''}`}
        >
          {members.map((member) => {
            const sub = member.subscription ?? undefined
            const subStatus = sub?.effective_status ?? undefined
            const children = checkInChildren(member)
            // A search that matched a child shows that child straight away.
            const isExpanded = children.length > 0 && (expandedIds.has(member.user_id!) || !!search)
            const name = memberDisplayName(member)

            return (
              <div key={member.id}>
                <MemberRow
                  avatar={
                    <MemberAvatar
                      userId={member.user_id}
                      name={name}
                      avatarExt={member.avatar_ext}
                      avatarUpdatedAt={member.avatar_updated_at}
                      primaryColor={primaryColor}
                      size="md"
                    />
                  }
                  name={
                    <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                      <MemberName
                        name={name}
                        nickname={member.nickname}
                        className="text-sm font-semibold"
                      />
                      {member.flags.is_club_member && <ClubMemberStar />}
                      {!member.user_id && (
                        <span className="rounded-full bg-default/15 px-1.5 py-px text-[11px] text-foreground/55">
                          {t('members.noAccountBadge')}
                        </span>
                      )}
                    </span>
                  }
                  onOpen={member.user_id ? () => setProfileTarget(member.user_id) : undefined}
                  openLabel={t('members.viewProfile')}
                  status={subStatus}
                  details={[
                    planLine(sub) ?? member.user_email,
                    children.length > 0 ? (
                      <ChildrenToggle
                        count={children.length}
                        isOpen={isExpanded}
                        onToggle={() => toggleExpand(member.user_id!)}
                      />
                    ) : null,
                  ]}
                  actions={
                    member.user_id
                      ? renderCheckInActions(
                          member.user_id,
                          subStatus,
                          sellButton(subStatus, {
                            memberEmail: member.user_email,
                            memberUserId: member.user_id,
                            memberName: name,
                          }),
                        )
                      : undefined
                  }
                />

                {isExpanded && (
                  <div className="mb-2 ml-3 border-l-2 border-divider pl-3 md:ml-12">
                    {children.map((child) => (
                      <MemberRow
                        key={child.id}
                        avatar={
                          <MemberAvatar
                            userId={null}
                            name={child.name}
                            avatarExt={null}
                            avatarUpdatedAt={null}
                            primaryColor={primaryColor}
                          />
                        }
                        name={<span className="text-sm font-medium">{child.name}</span>}
                        status={child.sub.effective_status ?? undefined}
                        details={[planLine(child.sub)]}
                        actions={renderChildCheckInActions(
                          member.user_id!,
                          child.id,
                          child.sub.effective_status ?? undefined,
                          sellButton(child.sub.effective_status ?? undefined, {
                            memberEmail: member.user_email,
                            memberUserId: member.user_id!,
                            childId: child.id,
                            childName: child.name,
                          }),
                        )}
                      />
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </Card>
      )}

      <ListPages
        page={page}
        pageSize={MEMBERS_PAGE_SIZE}
        total={total}
        onPageChange={goToPage}
        isLoading={loading}
      />

      {assignTarget && (
        <AssignPlanModal
          memberEmail={assignTarget.memberEmail}
          memberUserId={assignTarget.memberUserId}
          memberName={assignTarget.memberName}
          childId={assignTarget.childId}
          childName={assignTarget.childName}
          plans={plans}
          onAssigned={() => {
            setAssignTarget(null)
            void reload()
          }}
          onClose={() => setAssignTarget(null)}
        />
      )}

      {profileTarget && (
        <MemberProfileModal userId={profileTarget} onClose={() => setProfileTarget(null)} />
      )}
    </>
  )
}

// --- Main page ---

export function ScanPage() {
  const { t } = useTranslation()
  const { search } = useLocation()
  // In the URL so pull-to-refresh keeps the tab and the dashboard can link to history.
  const [activeTab, setActiveTab] = useTabParam('tab', SCAN_TABS, 'members')
  const isMobile = useIsMobile()
  const { canScan } = useQuickScan()

  // `?scanDebug=1` sticks until `?scanDebug=0`, so a gym can be put into
  // diagnostics mode by sending them one link.
  const debug = useMemo(() => syncScanDebugFromUrl(search), [search])

  useEffect(() => {
    scanBreadcrumb('check-in tab changed', { tab: activeTab, debug })
  }, [activeTab, debug])

  return (
    <div className="p-4 sm:p-8">
      <PageHeader title={t('scan.title')} subtitle={t('scan.subtitle')} />

      {debug && (
        <span className="mb-4 inline-block rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
          {t('scan.diagnosticsOn')}
        </span>
      )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <PageTabs
          ariaLabel={t('scan.title')}
          selectedKey={activeTab}
          onSelectionChange={setActiveTab}
          className=""
          items={SCAN_TABS.map((tab) => ({
            key: tab,
            label:
              tab === 'members'
                ? t('scan.membersTab')
                : tab === 'qr'
                  ? t('scan.qrTab')
                  : t('scan.historyTab'),
          }))}
        />
        {/* The USB scanner listens on every tab of this page; nothing to start. */}
        {!isMobile && (
          <Chip
            variant="flat"
            color="success"
            startContent={<span className="ml-1 h-2 w-2 animate-pulse rounded-full bg-success" />}
          >
            {t('scan.listening')}
          </Chip>
        )}
      </div>

      {/* Mounted once for the whole page so the USB scanner keeps listening
          across tabs. On Members and History it only shows a result banner;
          the Camera tab gets the camera. */}
      <QrScanner debug={debug} compact={activeTab !== 'qr'} source="check-in-page" />

      {/* A phone scans with its camera: one tap opens it full screen. */}
      {isMobile && canScan && activeTab === 'members' && (
        <ScanCard title={t('scan.cameraCard')} hint={t('scan.cameraCardHint')} />
      )}
      {activeTab === 'members' && <MembersCheckInList />}
      {activeTab === 'history' && <CheckInHistoryTab />}
    </div>
  )
}
